import React from 'react';
import { Link } from 'react-router-dom';

interface Category {
  id: string;
  name: string;
  description: string;
  icon: string;
  toolCount: number;
  slug?: string;
}

interface CategoryCardProps {
  category: Category;
}

function categoryToSlug(categoryName: string): string {
  return categoryName.toLowerCase().replace(/\s+/g, '-');
}

export default function CategoryCard({ category }: CategoryCardProps) {
  const slug = category.slug || categoryToSlug(category.name);

  return (
    <Link
      to={`/category/${slug}`}
      className="relative bg-[#111827] backdrop-blur-sm rounded-2xl p-7 border border-gray-800/80 hover:border-teal-500/40 transition-all duration-300 cursor-pointer group hover:shadow-[0_0_30px_rgba(20,184,166,0.15)] hover:scale-[1.02] flex flex-col overflow-hidden"
      style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}
    >
      {/* Top edge glow */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      {/* Icon */}
      <div className="w-16 h-16 flex items-center justify-center bg-teal-500/15 border border-teal-500/30 rounded-xl mb-4 group-hover:bg-teal-500/25 group-hover:border-teal-500/50 group-hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] transition-all duration-300 shrink-0">
        <i className={`${category.icon} text-3xl text-teal-300`}></i>
      </div>

      {/* Tool count badge */}
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-[11px] font-semibold mb-3 w-fit">
        <i className="ri-tools-line text-[10px]"></i>
        {category.toolCount} Tools
      </span>

      <h3 className="text-lg font-bold text-white mb-2.5 group-hover:text-teal-300 transition-colors duration-200 leading-snug">
        {category.name}
      </h3>

      <p className="text-gray-400 text-sm mb-5 leading-relaxed flex-1">
        {category.description}
      </p>

      <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-800/60">
        <span className="text-xs text-gray-500 group-hover:text-teal-400 transition-colors duration-200 font-medium">
          Explore category
        </span>
        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-500/15 group-hover:bg-teal-500/25 group-hover:scale-110 transition-all duration-300">
          <i className="ri-arrow-right-line text-teal-300 text-base group-hover:translate-x-0.5 transition-transform duration-200"></i>
        </span>
      </div>
    </Link>
  );
}
